/**
 * usePageChatConfig.ts
 *
 * Derives the dynamic GlobalChatSystem config for the current route.
 * Called once from App.tsx — the single app-level GlobalChatSystem reads this.
 *
 * Only the source, placeholder, welcomeMessage, and skipBalanceCheck
 * change per route.  All other GlobalChatSystem behaviour is uniform.
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { GlobalChatConfig } from '../components/GlobalChatSystem/types';
import { useAuth } from '../context/AuthContext';
import type { OrchestratorPageContext } from '../config/pageContextRegistry';
import {
  resolvePageContext,
  CHAT_EXCLUDED_ROUTES,
} from '../config/pageContextRegistry';
import { resolveThreadSwitcherCapability } from '../config/threadSwitcherCapability';

export interface PageChatConfigResult {
  /** Whether the GlobalChatSystem should render on this route */
  shouldRender: boolean;
  /** The fully resolved config to pass to <GlobalChatSystem config={...}> */
  config: Partial<GlobalChatConfig>;
  /** The rich page context for orchestrator page-awareness */
  pageContext: OrchestratorPageContext | undefined;
}

export function usePageChatConfig(): PageChatConfigResult {
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();

  return useMemo(() => {
    // P3 (persistent mount): excluded routes now resolve a FULL config like
    // any other route — shouldRender:false only means "render nothing
    // visible". AppGlobalChat maps it to config.hidden so the widget instance
    // (and all its in-memory state: conversation, drafts, streams) survives
    // crossing /auth or /onboarding instead of being unmounted and reborn.
    // Resolving the real config here is load-bearing: a bare {} would fall
    // back to DEFAULT_CHAT_CONFIG's legacy 'global_chat' instanceId and boot
    // the thread store into the wrong universe while hidden.
    const excluded = CHAT_EXCLUDED_ROUTES.has(pathname);

    const pageCtx = resolvePageContext(pathname);

    // Thread-switcher surfaces keep history PER THREAD in local storage
    // via useChatThreads. The separate ConversationHistoryContext Firestore
    // sync (useGlobalHistory) is a single-bucket doc per user; running both
    // at once on a thread-enabled surface causes cross-thread message bleed
    // (thread-B's "hi" gets rendered inside thread-A because the Firestore
    // listener force-syncs the flattened global doc back into React state).
    // Rule: threads ON => global history OFF; threads OFF => keep global
    // history ON so cross-device single-thread sync still works.
    const threadSwitcherEnabled = typeof pageCtx.enableThreadSwitcher === 'boolean'
      ? pageCtx.enableThreadSwitcher
      : resolveThreadSwitcherCapability({
          pathname,
          source: pageCtx.source,
          instanceId: 'site_chat',
          isAuthenticated,
        });

    const threadsOn = threadSwitcherEnabled;

    const config: Partial<GlobalChatConfig> = {
      instanceId: 'site_chat',
      source: pageCtx.source,
      placeholder: pageCtx.placeholder ?? 'ask prest0 anything…',
      allowAttachments: true,
      persistHistory: true,
      persistDraft: true,
      showWaitlistBubble: false,
      sendToPlatform: false,
      useGlobalHistory: !threadsOn,
      showDockButton: true,
      skipBalanceCheck: pageCtx.skipBalanceCheck ?? false,
      enableThreadSwitcher: threadSwitcherEnabled,
      ...(pageCtx.welcomeMessage ? { welcomeMessage: pageCtx.welcomeMessage } : {}),
      ...(pageCtx.welcomeStarters?.length ? { welcomeStarters: pageCtx.welcomeStarters } : {}),
    };

    // Build the rich page context for the orchestrator
    const pageContext: OrchestratorPageContext = {
      pathname,
      pageName: pageCtx.source.replace(/_/g, ' '),
      summary: pageCtx.summary,
      contextHint: pageCtx.contextHint,
    };

    return { shouldRender: !excluded, config, pageContext };
  }, [pathname, isAuthenticated]);
}
